import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import MaintenanceObjective from '@/common/components/MaintenanceObjective'
import ResumeItem from '@/common/components/ResumeItem'
import ReviewDescription from '@/common/components/ReviewDescription'
import { formatCurrency, formatDateTime } from '@/common/utils/formatters'
import DiagnosticEntryObjectiveActivities from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectiveActivities'
import DiagnosticEntryObjectivePieces from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectivePieces'
import {
  MaintenanceForwardingDirectiveEntryDiagnosticEntryData,
  MaintenanceForwardingDirectiveEntryStatus,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import useGetDirectiveEntryObjectivesSummary from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useGetDirectiveEntryObjectivesSummary'

interface DirectiveEntryDiagnosticEntryCompletedProps {
  diagnosticId: string
  lastReview: MaintenanceForwardingDirectiveEntryDiagnosticEntryData['last_review'] | undefined
  processStartedAt: string | null | undefined
  completedAt: string | null | undefined
  technicalResponse: string | undefined
  deadLineToCompleteInDays: number | null | undefined
  totalValue: string
}

const DirectiveEntryDiagnosticEntryCompleted = ({
  diagnosticId,
  lastReview,
  processStartedAt,
  completedAt,
  technicalResponse,
  deadLineToCompleteInDays,
  totalValue,
}: DirectiveEntryDiagnosticEntryCompletedProps) => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext()

  const directiveEntryObjectivesSummary = useGetDirectiveEntryObjectivesSummary()

  const isDiagnosticEntryDisapproved = lastReview?.state === 'disapproved'

  return (
    <Box className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex justify-between">
          <p className="font-semibold">{diagnosticId?.split('-')[0]?.toUpperCase()}</p>
        </div>

        {isDiagnosticEntryDisapproved && (
          <ReviewDescription
            description={lastReview?.description}
            showAlert={forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.InProgress}
          />
        )}

        {(processStartedAt || completedAt) && (
          <div className="flex gap-3">
            {processStartedAt && (
              <ResumeItem
                label="Diagnóstico e orçamento enviado"
                value={formatDateTime(new Date(processStartedAt))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {completedAt && (
              <ResumeItem
                label="Aprovado em"
                value={formatDateTime(new Date(completedAt))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {technicalResponse && (
          <ResumeItem
            label="Parecer técnico"
            value={technicalResponse}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        )}

        {deadLineToCompleteInDays && (
          <div className="flex gap-3">
            {deadLineToCompleteInDays && (
              <ResumeItem
                label="Prazo para execução do orçamento"
                value={`${deadLineToCompleteInDays} dias`}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>
        )}

        <div className="flex gap-3">
          <ResumeItem
            label="Total orçado em peças"
            value={`${directiveEntryObjectivesSummary.piecesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue || 0)}`}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <ResumeItem
            label="Total orçado em serviços"
            value={`${directiveEntryObjectivesSummary.activitiesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.activitiesTotalValue || 0)}`}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        </div>

        <div className="flex gap-3">
          <ResumeItem
            label="Valor total do orçamento"
            value={formatCurrency(parseFloat(totalValue) || 0)}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-base font-bold">Orçamento</p>

        {forwarding?.directive_entry?.objectives?.map((objective) => (
          <Accordion
            key={objective.id}
            defaultOpen
            className="p-0 rounded-lg"
            triggerClassName="p-4"
            bodyClassName="p-4 pt-0"
            head={
              <MaintenanceObjective
                systemDisplayName={objective.system.display_name}
                actionDisplayName={objective.action.display_name}
                partDisplayName={objective.part.display_name}
                actionDescription={objective.action.description}
              />
            }
            body={
              <div className="flex flex-col gap-3">
                <DiagnosticEntryObjectivePieces objective={objective} dataUpdatedAt={forwardingUpdatedAt} />

                <DiagnosticEntryObjectiveActivities objective={objective} dataUpdatedAt={forwardingUpdatedAt} />
              </div>
            }
          />
        ))}
      </div>
    </Box>
  )
}

export default DirectiveEntryDiagnosticEntryCompleted
